import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { browserOnPhone } from "lib/browser-detect";
import React from "react";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { SectionStyleProvider } from "webapp/ui/views/wire-container/wire-container";
import { WireMenuButton } from "webapp/ui/views/wire-menu-button/wire-menu-button";
import { breakoutActions, makeActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import type { UIImageFill } from "wire/types";
import {
    UIAlignment,
    UIAspect,
    UIBackgroundStyle,
    UIButtonAppearance,
    UIImageStyle,
    UISize,
    UnboundVal,
} from "wire/types";
import Img from "../img/img";
import type { CardProps } from "./card-collection-types";

export const CardContainer: React.FC<{ size?: UISize }> = p => {
    const { children, size } = p;

    return (
        <div
            tw="grid w-full"
            className={size}
            css={css`
                ${tw`gap-4`}
                &.${UISize.XSmall} {
                    ${tw`grid-cols-2
                      gp-sm:grid-cols-3
                      gp-md:(gap-5 grid-cols-4)
                      gp-lg:(grid-cols-5)
                      gp-xl:grid-cols-6`}
                }
                &.${UISize.Small} {
                    ${tw`grid-cols-2
                      gp-sm:grid-cols-3
                      gp-md:(gap-5 grid-cols-4)
                      gp-lg:gap-6
                      gp-xl:grid-cols-5`}
                }
                &.${UISize.Medium} {
                    ${tw`text-lg grid-cols-1
                      gp-sm:(gap-5 grid-cols-2)
                      gp-md:grid-cols-3
                      gp-lg:(gap-6 grid-cols-4)`}
                }
                &.${UISize.Large} {
                    ${tw`text-lg
                      gp-sm:gap-5
                      gp-md:gap-6
                      gp-md:grid-cols-2
                      gp-lg:grid-cols-3`}
                }
                &.${UISize.XLarge} {
                    ${tw`text-lg
                      gp-sm:gap-5
                      gp-md:(gap-6 grid-cols-1)
                      gp-lg:grid-cols-2`}
                }
            `}>
            {children}
        </div>
    );
};

export const Card: React.VFC<
    CardProps & {
        size: UISize;
        imageSize: UISize;
        imageFill: UIImageFill;
        alignment: UIAlignment;
        aspect: UIAspect;
        viewType: "card" | "grid";
    }
> = p => {
    const { desc, imageStyle, alignment, imageSize, aspect, viewType, imageFill } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);

    const imageStyleSizeClass = `${imageStyle}-${imageSize}`;

    // FIME: Is this text style correct for both card and grid
    const text = (
        <>
            {desc.emphasis && (
                <div
                    tw="text-xs line-height[20px] uppercase font-bold"
                    className={viewType}
                    css={css`
                        &.card {
                            ${tw`text-accent`}
                        }
                        &.grid {
                            ${tw`text-text-contextual-accent`}
                        }
                    `}>
                    {desc.emphasis}
                </div>
            )}
            {desc.title && (
                <div
                    tw="font-bold leading-tight whitespace-pre-wrap"
                    className={viewType}
                    css={css`
                        &.card {
                            ${tw`text-text-dark`}
                        }
                        &.grid {
                            ${tw`text-text-contextual-dark`}
                        }
                    `}>
                    {desc.title}
                </div>
            )}
            {desc.subtitle && (
                <div
                    tw="text-sm whitespace-pre-wrap"
                    className={viewType}
                    css={css`
                        &.card {
                            ${tw`text-text-pale`}
                        }
                        &.grid {
                            ${tw`text-text-contextual-pale`}
                        }
                    `}>
                    {desc.subtitle}
                </div>
            )}
        </>
    );

    const buttons = (
        <div
            tw="flex w-full"
            onClick={e => e.stopPropagation()}
            className={classNames(viewType, { mobile: browserOnPhone })}
            css={css`
                &.card {
                    ${tw`pr-4 pb-4 pl-4`}
                }

                &.grid:not(.mobile) {
                    ${tw`opacity-0 group-hover:opacity-100`}
                }

                &.grid {
                    ${tw`absolute bottom-0 pr-3 pb-3 pl-3 rounded-b-lg transition`}
                }

                .${UISize.XSmall} & {
                    ${tw`flex-col`}
                }
            `}>
            {/* FIXME: Collapse styled button flow for small widths */}
            {primary !== undefined && (
                <WireButton
                    tw="mt-2 flex-grow"
                    data-testid="card-primary-button"
                    appearance={UIButtonAppearance.Filled}
                    {...makeActionSpreadProps(primary)}
                />
            )}
            {secondary !== undefined && (
                <>
                    <div tw="w-2 flex-shrink-0"></div>
                    <WireButton
                        tw="ml-0 mt-2 flex-grow"
                        appearance={UIButtonAppearance.Bordered}
                        {...makeActionSpreadProps(secondary)}
                    />
                </>
            )}
        </div>
    );

    const imageContainerRef = React.createRef<HTMLDivElement>();

    const [parentHeight, setParentHeight] = React.useState<number>(0);

    React.useEffect(() => {
        if (imageContainerRef.current) {
            setParentHeight(imageContainerRef.current.offsetHeight);
        }
    }, [imageContainerRef]);

    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <div
                data-testid="cc-card"
                onClick={e => {
                    desc.onCardClick?.();
                    e.stopPropagation();
                }}
                tw="relative text-text-dark flex flex-col w-full rounded-lg transition-shadow cursor-default"
                className={classNames(viewType, "group", desc.onCardClick && "has-click-action")}
                css={css`
                    &.card {
                        ${tw`bg-n0 shadow-md overflow-hidden`}
                    }
                    &.card.has-click-action {
                        ${tw`hover:shadow-xl-dark`}
                    }

                    &.has-click-action {
                        ${tw`cursor-pointer`}
                    }
                `}>
                {desc.image !== UnboundVal && (
                    <div
                        tw="relative w-full min-height[72px] padding-top[56.25%]"
                        className={classNames(alignment, aspect, imageStyleSizeClass)}
                        css={css`
                            &.${UIAspect.Auto}, &.${UIAspect.SixteenByNine} {
                                ${tw`padding-top[56.25%]`}
                            }

                            &.${UIAspect.Square} {
                                ${tw`padding-top[100%]`}
                            }

                            &.${UIAspect.FourByThree} {
                                ${tw`padding-top[75%]`}
                            }

                            &.${UIAspect.ThreeByOne} {
                                ${tw`padding-top[33.33%]`}
                            }

                            &.${UIAspect.ThreeByTwo} {
                                ${tw`padding-top[66.66%]`}
                            }

                            &.${UIImageStyle.Circle}-${UISize.XSmall} {
                                ${tw`padding-top[33.33%]`}
                            }

                            &.${UIImageStyle.Circle}-${UISize.Small} {
                                ${tw`padding-top[50%]`}
                            }

                            &.${UIImageStyle.Circle}-${UISize.Medium} {
                                ${tw`padding-top[66.66%]`}
                            }

                            &.${UIImageStyle.Circle}-${UISize.Large} {
                                ${tw`padding-top[100%]`}
                            }
                        `}>
                        <div
                            tw="absolute top-0 h-full w-full flex justify-center"
                            css={css`
                                .${UIAlignment.Start} & {
                                    ${tw`justify-start`}
                                }
                                .${UIImageStyle.Circle}-${UISize.XSmall} & {
                                    ${tw`p-4`}
                                }

                                .${UIImageStyle.Circle}-${UISize.Small} &,
                                .${UIImageStyle.Circle}-${UISize.Medium} & {
                                    ${tw`p-5`}
                                }

                                .${UIImageStyle.Circle}-${UISize.Large} & {
                                    ${tw`p-6`}
                                }
                            `}>
                            {imageStyle === UIImageStyle.Circle && (
                                <div tw="relative" ref={imageContainerRef}>
                                    <Img
                                        src={massageImageUrl(
                                            desc.image ?? undefined,
                                            {
                                                thumbnail: false,
                                            },
                                            undefined
                                        )}
                                        css={css`
                                            width: ${parentHeight}px;
                                        `}
                                        tw="relative inset-0 h-full object-cover bg-n50 transition-shadow rounded-full"
                                    />
                                </div>
                            )}
                            {imageStyle === UIImageStyle.Rectilinear && (
                                <Img
                                    src={massageImageUrl(
                                        desc.image ?? undefined,
                                        {
                                            thumbnail: false,
                                        },
                                        undefined
                                    )}
                                    tw="h-full w-full object-cover bg-n50 transition-shadow"
                                    className={classNames(imageStyleSizeClass, viewType, imageFill)}
                                    css={css`
                                        &.grid {
                                            ${tw`rounded-lg group-hover:shadow-xl`}
                                        }

                                        &.image-circle {
                                            ${tw`object-cover rounded-full`}
                                        }

                                        &.image-fill-contain {
                                            ${tw`object-contain`}
                                        }
                                    `}
                                />
                            )}
                        </div>
                        {viewType === "grid" && buttons}
                    </div>
                )}
                {desc.heroImage && (
                    <Img
                        src={massageImageUrl(desc.heroImage, { thumbnail: false, width: 96, height: 96 }, undefined)}
                        tw="w-24 h-24 self-center -mt-12 flex-shrink-0 rounded-full object-cover border-2 z-10
                            border-solid border-n0"
                    />
                )}
                {menuActions.length > 0 && (
                    <WireMenuButton
                        tw="absolute top-4 right-4"
                        className={classNames(viewType)}
                        css={css`
                            &.grid {
                                ${tw`opacity-0 group-hover:opacity-100 transition-opacity top-3 right-3`}
                            }
                        `}
                        appearance={UIButtonAppearance.Bordered}
                        menuItems={menuActions}
                    />
                )}
                <div
                    tw="flex-grow flex flex-col pt-4 pr-4 pl-4"
                    className={classNames(imageStyle, alignment, viewType)}
                    css={css`
                        &.grid {
                            ${tw`pl-0 pr-0`}
                        }

                        &.${UIImageStyle.Circle} {
                            ${tw`pt-0`}
                        }

                        &.${UIAlignment.Center} {
                            ${tw`items-center`}
                        }
                    `}>
                    {text}
                    <div tw="flex-grow" />
                </div>
                {viewType === "card" && buttons}
            </div>
        </SectionStyleProvider>
    );
};
